import PropTypes from 'prop-types';
import React, { Component } from 'react';

class AnimatedArc extends Component {
  constructor(props) {
    super(props);
    const { targetOuterRadius } = this.props;
    this.state = {
      currentOuterRadius: targetOuterRadius,
    };
    this.animationFrame = null;
  }

  componentDidUpdate(prevProps) {
    const { targetOuterRadius } = this.props;
    if (prevProps.targetOuterRadius !== targetOuterRadius) {
      this.animateTo(targetOuterRadius);
    }
  }

  componentWillUnmount() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  animateTo(target) {
    const { currentOuterRadius } = this.state;
    const duration = 100; // in ms
    const startTime = performance.now();
    const startValue = currentOuterRadius;
    const animateStep = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const newVal = startValue + (target - startValue) * progress;
      this.setState({ currentOuterRadius: newVal });
      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animateStep);
      }
    };
    this.animationFrame = requestAnimationFrame(animateStep);
  }

  polarToCartesian(cx, cy, r, angle) {
    return {
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle),
    };
  }

  describeArc(cx, cy, innerRadius, outerRadius, startAngle, endAngle) {
    const isFullCircle =
      Math.abs(endAngle - startAngle) >= 2 * Math.PI - 0.0001;

    if (isFullCircle) {
      // Draw full ring as a single compound path with two arcs
      const midAngle = startAngle + Math.PI;

      const startOuter = this.polarToCartesian(cx, cy, outerRadius, startAngle);
      const midOuter = this.polarToCartesian(cx, cy, outerRadius, midAngle);
      const endOuter = this.polarToCartesian(cx, cy, outerRadius, endAngle);

      const startInner = this.polarToCartesian(cx, cy, innerRadius, endAngle);
      const midInner = this.polarToCartesian(cx, cy, innerRadius, midAngle);
      const endInner = this.polarToCartesian(cx, cy, innerRadius, startAngle);

      return [
        'M',
        startOuter.x,
        startOuter.y,
        'A',
        outerRadius,
        outerRadius,
        0,
        1,
        1,
        midOuter.x,
        midOuter.y,
        'A',
        outerRadius,
        outerRadius,
        0,
        1,
        1,
        endOuter.x,
        endOuter.y,
        'L',
        startInner.x,
        startInner.y,
        'A',
        innerRadius,
        innerRadius,
        0,
        1,
        0,
        midInner.x,
        midInner.y,
        'A',
        innerRadius,
        innerRadius,
        0,
        1,
        0,
        endInner.x,
        endInner.y,
        'Z',
      ].join(' ');
    }

    return this._arcPath(
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle
    );
  }

  // Helper to generate an arc path between any two angles
  _arcPath(cx, cy, innerRadius, outerRadius, startAngle, endAngle) {
    const startOuter = this.polarToCartesian(cx, cy, outerRadius, startAngle);
    const endOuter = this.polarToCartesian(cx, cy, outerRadius, endAngle);
    const startInner = this.polarToCartesian(cx, cy, innerRadius, endAngle);
    const endInner = this.polarToCartesian(cx, cy, innerRadius, startAngle);
    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;

    return [
      'M',
      startOuter.x,
      startOuter.y,
      'A',
      outerRadius,
      outerRadius,
      0,
      largeArcFlag,
      1,
      endOuter.x,
      endOuter.y,
      'L',
      startInner.x,
      startInner.y,
      'A',
      innerRadius,
      innerRadius,
      0,
      largeArcFlag,
      0,
      endInner.x,
      endInner.y,
      'Z',
    ].join(' ');
  }

  render() {
    const {
      cx,
      cy,
      innerRadius,
      startAngle,
      endAngle,
      fill,
      stroke,
      strokeWidth,
      onMouseOver,
      onFocus,
    } = this.props;
    const { currentOuterRadius } = this.state;
    const path = this.describeArc(
      cx,
      cy,
      innerRadius,
      currentOuterRadius,
      startAngle,
      endAngle
    );
    return (
      <path
        d={path}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        onMouseOver={onMouseOver}
        onFocus={onFocus}
      />
    );
  }
}

export default AnimatedArc;

AnimatedArc.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  innerRadius: PropTypes.number.isRequired,
  startAngle: PropTypes.number.isRequired,
  endAngle: PropTypes.number.isRequired,
  fill: PropTypes.string.isRequired,
  stroke: PropTypes.string,
  strokeWidth: PropTypes.number,
  onMouseOver: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  targetOuterRadius: PropTypes.number.isRequired,
};

AnimatedArc.defaultProps = {
  stroke: '#fff',
  strokeWidth: 1,
};
